import React, { useState, useRef } from "react";
import { Link } from "gatsby";
import { ChevronLeftIcon, ChevronRightIcon } from "@heroicons/react/outline";
import { generateEpisodeSlug } from "../../../utilities/generateEpisodeSlug";

export default function SectionAdkins({ slice, location }) {
  const [activeFilter, setActiveFilter] = useState("all");
  const scrollContainerRef = useRef(null);

  // Extract base path from current location, removing leading/trailing slashes
  const basePath =
    location?.pathname?.replace(/^\/|\/$/g, "") || "ai-data-chats";

  const filterTags = [
    ...new Set(
      slice.items.map((item) => item.filter_tag?.text).filter(Boolean),
    ),
  ];

  // Filter items based on active filter
  const filteredItems =
    activeFilter === "all"
      ? slice.items
      : slice.items.filter((item) => item.filter_tag?.text === activeFilter);

  const scrollLeft = () => {
    if (scrollContainerRef.current) {
      scrollContainerRef.current.scrollBy({ left: -400, behavior: "smooth" });
    }
  };

  const scrollRight = () => {
    if (scrollContainerRef.current) {
      scrollContainerRef.current.scrollBy({ left: 400, behavior: "smooth" });
    }
  };

  return (
    <div className="bg-[#F2F3F5]" id="section-adkins">
      <div className="md:max-w-7xl px-6 xl:px-16 mx-auto pt-10 md:py-10 ">
        {/* Section Title */}
        <div className="mb-6">
          <h2 className="font-manrope text-2.5xl md:text-3.25xl font-medium text-gray-900 mb-4">
            {slice.primary.title?.text || "Let's AI Data Chat"}
          </h2>
          <p className="font-inter text-base text-gray-600 max-w-2xl">
            {slice.primary.descriptor_text?.text}
          </p>
        </div>

        {/* Filter Tabs */}
        <div className="mb-6">
          <div className="flex">
            {/* All filter */}
            {filterTags.length > 1 && (
              <button
                onClick={() => setActiveFilter("all")}
                className={`relative pb-3 px-4 font-inter text-base md:text-lg font-medium transition-all duration-500 ease-out overflow-hidden ${
                  activeFilter === "all"
                    ? "text-gray-900"
                    : "text-gray-500 hover:text-gray-700"
                }`}
              >
                All
                {/* Base border line */}
                <div className="absolute bottom-0 left-0 right-0 h-0.5 bg-gray-300"></div>
                <div
                  className={`absolute bottom-0 h-0.5 bg-[#5E44FF] transition-all duration-500 ease-out ${
                    activeFilter === "all"
                      ? "left-0 right-0 opacity-100"
                      : "left-1/2 right-1/2 opacity-0"
                  }`}
                >
                  {/* Shimmer effect overlay */}
                  <div
                    className={`absolute inset-0 bg-gradient-to-r from-transparent via-white to-transparent opacity-30 transition-transform duration-1000 ${
                      activeFilter === "all"
                        ? "translate-x-full"
                        : "-translate-x-full"
                    }`}
                    style={{
                      animation:
                        activeFilter === "all" ? "shimmer 2s infinite" : "none",
                    }}
                  />
                </div>
              </button>
            )}

            {/* Dynamic filter tabs */}
            {filterTags.length > 1 &&
              filterTags.map((tag) => (
                <button
                  key={tag}
                  onClick={() => setActiveFilter(tag)}
                  className={`relative pb-3 px-4 font-inter text-base md:text-lg  font-medium transition-all duration-500 ease-out overflow-hidden ${
                    activeFilter === tag
                      ? "text-gray-900"
                      : "text-gray-500 hover:text-gray-700"
                  }`}
                >
                  {tag}
                  {/* Base border line */}
                  <div className="absolute bottom-0 left-0 right-0 h-0.5 bg-gray-300"></div>
                  <div
                    className={`absolute bottom-0 h-0.5 bg-[#5E44FF] transition-all duration-500 ease-out ${
                      activeFilter === tag
                        ? "left-0 right-0 opacity-100"
                        : "left-1/2 right-1/2 opacity-0"
                    }`}
                  >
                    {/* Shimmer effect overlay */}
                    <div
                      className={`absolute inset-0 bg-gradient-to-r from-transparent via-white to-transparent opacity-30 transition-transform duration-1000 ${
                        activeFilter === tag
                          ? "translate-x-full"
                          : "-translate-x-full"
                      }`}
                      style={{
                        animation:
                          activeFilter === tag ? "shimmer 2s infinite" : "none",
                      }}
                    />
                  </div>
                </button>
              ))}
          </div>
        </div>

        {/* Horizontal Scrolling Container */}
        <div>
          {/* Scrolling Container */}
          <div
            ref={scrollContainerRef}
            className="overflow-x-auto scrollbar-hide scroll-smooth rounded-lg"
            style={{ scrollbarWidth: "none", msOverflowStyle: "none" }}
          >
            <div className="flex space-x-6 pb-4 snap-x snap-mandatory lg:min-h-[370px]">
              {filteredItems.map((item, index) => (
                <div
                  key={index}
                  className="flex-shrink-0 w-72 md:w-[400px] rounded-lg overflow-hidden snap-start transition-all duration-300"
                >
                  <Link
                    to={`/${basePath}/${generateEpisodeSlug(item)}`}
                    className="block group"
                  >
                    {/* Thumbnail Image */}
                    <div className="aspect-video bg-gray-200 overflow-hidden relative rounded-lg">
                      {item.video_thumbnail?.url && (
                        <>
                          <img
                            src={item.video_thumbnail.url}
                            alt={item.video_thumbnail.alt || "Video thumbnail"}
                            className="w-full h-full object-cover rounded-lg transition-transform duration-500 ease-out"
                          />
                          {/* Subtle overlay on hover */}
                          <div className="absolute inset-0 bg-black opacity-0 rounded-lg group-hover:opacity-10 transition-opacity duration-300" />
                        </>
                      )}
                    </div>
                    {/* Content */}
                    <div className="pt-2 lg:pt-[14px] min-h-32">
                      {/* Eyebrow */}
                      <p className="font-inter text-xs lg:text-base font-medium text-gray-500 mb-2 lg:mb-2 group-hover:text-[#262626] transition-colors duration-300">
                        {item.eyebrow?.text}
                      </p>

                      {/* Title */}
                      <h3 className="font-manrope text-lg lg:text-xl font-medium text-gray-2700 leading-tight group-hover:text-[#171717] transition-colors duration-300">
                        {item.thumbnail_title?.text}
                      </h3>
                    </div>
                  </Link>
                </div>
              ))}
            </div>
          </div>

          {/* Navigation Buttons - Hidden on mobile */}
          <div className="hidden md:flex space-x-3">
            <button
              onClick={scrollLeft}
              className="text-white w-8 h-8 flex items-center justify-center bg-[#9F9F9F] opacity-50 rounded-full hover:opacity-30 transition-all duration-300"
            >
              <ChevronLeftIcon className="w-6 h-6" />
            </button>
            <button
              onClick={scrollRight}
              className="text-white w-8 h-8 flex items-center justify-center bg-[#9F9F9F] opacity-50 rounded-full hover:opacity-30 transition-all duration-300"
            >
              <ChevronRightIcon className="w-6 h-6" />
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
